import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../config/api';
import toast from 'react-hot-toast';
import * as ps from '../services/pocketsmith.service';
import { tw, monoFont } from '../utils/terminalTheme';

interface Property {
  id: number;
  name: string;
  address: string;
}

const buildRuleDescription = (rule: ps.MatchRule): string => {
  const parts: string[] = [];
  if (rule.payee_contains) parts.push(`payee contains "${rule.payee_contains}"`);
  if (rule.account_name_contains) parts.push(`account contains "${rule.account_name_contains}"`);
  if (rule.category_contains) parts.push(`category contains "${rule.category_contains}"`);
  if (rule.amount_min != null) parts.push(`amount >= $${rule.amount_min}`);
  if (rule.amount_max != null) parts.push(`amount <= $${rule.amount_max}`);
  if (rule.note_contains) parts.push(`note contains "${rule.note_contains}"`);
  if (parts.length === 0) return 'No criteria set';
  return parts.join(' AND ');
};

export const PocketSmithRules: React.FC = () => {
  const [rules, setRules] = useState<ps.MatchRule[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [applyingRules, setApplyingRules] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);

  // New rule form
  const [newPayee, setNewPayee] = useState('');
  const [newAccount, setNewAccount] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newAmountMin, setNewAmountMin] = useState('');
  const [newAmountMax, setNewAmountMax] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newPropertyId, setNewPropertyId] = useState<number>(0);
  const [newLabel, setNewLabel] = useState('');

  // Edit form
  const [editPayee, setEditPayee] = useState('');
  const [editAccount, setEditAccount] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editAmountMin, setEditAmountMin] = useState('');
  const [editAmountMax, setEditAmountMax] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editPropertyId, setEditPropertyId] = useState<number>(0);
  const [editLabel, setEditLabel] = useState('');

  const fetchRules = useCallback(async () => {
    try {
      const result = await ps.getRules();
      setRules(result.data || []);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const propsRes = await api.get('/properties/').catch(() => ({ data: [] }));
        setProperties((propsRes.data || []).sort((a: any, b: any) => (a.address || a.name || '').localeCompare(b.address || b.name || '')));
        await fetchRules();
      } catch (err) {
        console.error('Init error:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchRules]);

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Delete this rule? This cannot be undone.')) return;
    try {
      await ps.deleteRule(ruleId);
      toast.success('Rule deleted');
      setEditingRuleId(null);
      await fetchRules();
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const handleApplyRules = async () => {
    setApplyingRules(true);
    try {
      const result = await ps.applyRules();
      if (result.matched > 0) {
        toast.success(`Matched ${result.matched} of ${result.total_unmatched} unmatched transactions (${result.rules_checked} rules checked)`);
      } else {
        toast.success(`No new matches found (checked ${result.rules_checked} rules against ${result.total_unmatched} unmatched transactions)`);
      }
      await fetchRules();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to apply rules');
    } finally {
      setApplyingRules(false);
    }
  };

  const handleCreateRule = async () => {
    if (!newPayee && !newAccount && !newCategory) {
      toast.error('At least one match criterion is required (payee, account, or category)');
      return;
    }
    if (!newPropertyId) {
      toast.error('Please select a property');
      return;
    }
    try {
      await ps.createRule({
        payee_contains: newPayee || undefined,
        account_name_contains: newAccount || undefined,
        category_contains: newCategory || undefined,
        amount_min: newAmountMin ? parseFloat(newAmountMin) : undefined,
        amount_max: newAmountMax ? parseFloat(newAmountMax) : undefined,
        note_contains: newNote || undefined,
        property_id: newPropertyId,
        label: newLabel || undefined,
      });
      toast.success('Rule created');
      setNewPayee('');
      setNewAccount('');
      setNewCategory('');
      setNewAmountMin('');
      setNewAmountMax('');
      setNewNote('');
      setNewPropertyId(0);
      setNewLabel('');
      setShowAddForm(false);
      await fetchRules();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to create rule');
    }
  };

  const startEditing = (rule: ps.MatchRule) => {
    setEditingRuleId(rule.id);
    setEditPayee(rule.payee_contains || '');
    setEditAccount(rule.account_name_contains || '');
    setEditCategory(rule.category_contains || '');
    setEditAmountMin(rule.amount_min != null ? String(rule.amount_min) : '');
    setEditAmountMax(rule.amount_max != null ? String(rule.amount_max) : '');
    setEditNote(rule.note_contains || '');
    setEditPropertyId(rule.property_id);
    setEditLabel(rule.label || '');
  };

  const handleSaveEdit = async () => {
    if (!editingRuleId) return;
    if (!editPayee && !editAccount && !editCategory) {
      toast.error('At least one match criterion is required');
      return;
    }
    if (!editPropertyId) {
      toast.error('Please select a property');
      return;
    }
    try {
      await ps.updateRule(editingRuleId, {
        payee_contains: editPayee || null,
        account_name_contains: editAccount || null,
        category_contains: editCategory || null,
        amount_min: editAmountMin ? parseFloat(editAmountMin) : 0,
        amount_max: editAmountMax ? parseFloat(editAmountMax) : 0,
        note_contains: editNote || null,
        property_id: editPropertyId,
        label: editLabel,
      });
      toast.success('Rule updated');
      setEditingRuleId(null);
      await fetchRules();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update rule');
    }
  };

  const getPropertyName = (propertyId: number) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? (prop.address || prop.name) : `Property #${propertyId}`;
  };

  if (loading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className={tw.pageWide}>
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={monoFont}>
            <span className="text-cyan-400">~/</span>pocketsmith/rules
          </h1>
          <p className="text-gray-500 text-sm font-mono mt-1">
            <span className="text-green-500">$</span> {rules.length} active rule{rules.length !== 1 ? 's' : ''}
            <span className="text-gray-600 mx-2">|</span>
            auto-match transactions to properties
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { setShowAddForm(!showAddForm); setEditingRuleId(null); }}
            className={showAddForm ? tw.btnSecondary : tw.btnPrimary}
          >
            {showAddForm ? '[ cancel ]' : '+ new rule'}
          </button>
          <button
            onClick={handleApplyRules}
            disabled={applyingRules || rules.length === 0}
            className={`font-mono text-sm px-4 py-2 rounded-md transition-colors ${
              rules.length === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer'
            } ${applyingRules ? 'opacity-60' : ''}`}
          >
            {applyingRules ? 'applying...' : 'apply rules to unmatched'}
          </button>
        </div>
      </div>

      {/* Add Rule Form */}
      {showAddForm && (
        <div className={tw.formWrap + ' mb-6'}>
          <h3 className={tw.formTitle}>create new rule</h3>
          <p className="text-gray-500 text-xs font-mono mb-5">
            All text criteria use <span className="text-gray-300">case-insensitive substring matching</span>.
            Amount filters match the absolute value. When multiple criteria are set, they're combined with AND logic.
          </p>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className={tw.label}>Payee Contains</label>
              <input type="text" value={newPayee} onChange={e => setNewPayee(e.target.value)}
                placeholder="e.g. FPL" className={tw.input} />
            </div>
            <div>
              <label className={tw.label}>Account Name Contains</label>
              <input type="text" value={newAccount} onChange={e => setNewAccount(e.target.value)}
                placeholder="e.g. Novo" className={tw.input} />
            </div>
            <div>
              <label className={tw.label}>Category Contains</label>
              <input type="text" value={newCategory} onChange={e => setNewCategory(e.target.value)}
                placeholder="e.g. Utilities" className={tw.input} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className={tw.label}>Amount Min ($)</label>
              <input type="number" value={newAmountMin} onChange={e => setNewAmountMin(e.target.value)}
                placeholder="e.g. 500" step="0.01" min="0" className={tw.input} />
            </div>
            <div>
              <label className={tw.label}>Amount Max ($)</label>
              <input type="number" value={newAmountMax} onChange={e => setNewAmountMax(e.target.value)}
                placeholder="e.g. 2000" step="0.01" min="0" className={tw.input} />
            </div>
            <div>
              <label className={tw.label}>Note/Keyword Contains</label>
              <input type="text" value={newNote} onChange={e => setNewNote(e.target.value)}
                placeholder="e.g. rent" className={tw.input} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <label className={tw.label}>Assign to Property *</label>
              <select value={newPropertyId} onChange={e => setNewPropertyId(parseInt(e.target.value) || 0)} className={tw.select}>
                <option value={0}>-- Select Property --</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.address || p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={tw.label}>Label (optional description)</label>
              <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
                placeholder="e.g. FPL electric bill" className={tw.input} />
            </div>
          </div>
          <button onClick={handleCreateRule} className={tw.btnPrimary}>
            create rule
          </button>
        </div>
      )}

      {/* How it works */}
      <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5 mb-6">
        <h3 className="text-cyan-400 font-mono text-sm mb-3">how rules work</h3>
        <div className="grid grid-cols-4 gap-6">
          <div>
            <p className="text-gray-500 text-xs font-mono m-0">
              <span className="text-gray-400 font-semibold">Text Matching</span> — Rules use <span className="text-cyan-400">case-insensitive substring</span> matching. "FPL" matches any payee containing "fpl", "FPL GROUP", etc.
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-xs font-mono m-0">
              <span className="text-gray-400 font-semibold">AND Logic</span> — If a rule has multiple criteria, <span className="text-cyan-400">all must match</span> for the rule to fire.
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-xs font-mono m-0">
              <span className="text-gray-400 font-semibold">Amount Filter</span> — Amount min/max match against the <span className="text-cyan-400">absolute value</span> of the transaction amount.
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-xs font-mono m-0">
              <span className="text-gray-400 font-semibold">Auto-Apply</span> — Rules run after every sync and can be triggered manually on unmatched transactions.
            </p>
          </div>
        </div>
      </div>

      {/* Rules List */}
      <div className={tw.tableWrap}>
        <div className="px-5 py-4 border-b border-gray-700/50 flex justify-between items-center">
          <h2 className="text-white font-mono text-lg m-0" style={monoFont}>
            active rules ({rules.length})
          </h2>
        </div>

        {rules.length === 0 ? (
          <div className={tw.empty}>
            <p className="text-base font-mono mb-2">no rules yet</p>
            <p className="text-xs font-mono">
              Go to Transactions, match a transaction to a property, and click "Create Rule" — or use the "+ new rule" button above.
            </p>
          </div>
        ) : (
          <div className={tw.tableDivider}>
            {rules.map(rule => (
              <div key={rule.id} className="border-t border-gray-700/30">
                {/* Rule summary row */}
                <div className="px-5 py-4 flex justify-between items-start gap-4 hover:bg-gray-700/20 transition-colors group">
                  <div className="flex-1">
                    {/* Rule description */}
                    <div className="mb-2 flex flex-wrap items-center gap-1">
                      <span className="text-gray-400 text-sm font-mono">When </span>
                      {rule.payee_contains && (
                        <>
                          <span className="text-gray-400 text-sm font-mono">payee contains </span>
                          <span className="bg-blue-500/15 text-blue-400 px-2 py-px rounded text-xs font-mono border border-blue-500/30">
                            {rule.payee_contains}
                          </span>
                        </>
                      )}
                      {rule.payee_contains && (rule.account_name_contains || rule.category_contains || rule.amount_min != null || rule.amount_max != null || rule.note_contains) && (
                        <span className="text-gray-600 text-xs font-mono"> AND </span>
                      )}
                      {rule.account_name_contains && (
                        <>
                          <span className="text-gray-400 text-sm font-mono">account contains </span>
                          <span className="bg-teal-500/15 text-teal-400 px-2 py-px rounded text-xs font-mono border border-teal-500/30">
                            {rule.account_name_contains}
                          </span>
                        </>
                      )}
                      {rule.account_name_contains && (rule.category_contains || rule.amount_min != null || rule.amount_max != null || rule.note_contains) && (
                        <span className="text-gray-600 text-xs font-mono"> AND </span>
                      )}
                      {rule.category_contains && (
                        <>
                          <span className="text-gray-400 text-sm font-mono">category contains </span>
                          <span className="bg-purple-500/15 text-purple-400 px-2 py-px rounded text-xs font-mono border border-purple-500/30">
                            {rule.category_contains}
                          </span>
                        </>
                      )}
                      {rule.category_contains && (rule.amount_min != null || rule.amount_max != null || rule.note_contains) && (
                        <span className="text-gray-600 text-xs font-mono"> AND </span>
                      )}
                      {(rule.amount_min != null || rule.amount_max != null) && (
                        <>
                          <span className="text-gray-400 text-sm font-mono">amount </span>
                          <span className="bg-amber-500/15 text-amber-400 px-2 py-px rounded text-xs font-mono border border-amber-500/30">
                            {rule.amount_min != null && rule.amount_max != null
                              ? `$${rule.amount_min} - $${rule.amount_max}`
                              : rule.amount_min != null
                              ? `>= $${rule.amount_min}`
                              : `<= $${rule.amount_max}`}
                          </span>
                        </>
                      )}
                      {(rule.amount_min != null || rule.amount_max != null) && rule.note_contains && (
                        <span className="text-gray-600 text-xs font-mono"> AND </span>
                      )}
                      {rule.note_contains && (
                        <>
                          <span className="text-gray-400 text-sm font-mono">note contains </span>
                          <span className="bg-cyan-500/15 text-cyan-400 px-2 py-px rounded text-xs font-mono border border-cyan-500/30">
                            {rule.note_contains}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Arrow + property */}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 text-base font-mono">&#8594;</span>
                      <span className="text-indigo-400 text-sm font-mono font-medium">
                        Assign to {getPropertyName(rule.property_id)}
                      </span>
                    </div>

                    {/* Label if present */}
                    {rule.label && (
                      <div className="text-gray-600 text-xs font-mono mt-1 italic">
                        {rule.label}
                      </div>
                    )}
                  </div>

                  {/* Right side: match count + actions */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`px-3 py-1 rounded-full text-xs font-mono font-semibold whitespace-nowrap ${
                      rule.match_count > 0
                        ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                        : 'bg-gray-800/80 text-gray-600'
                    }`}>
                      {rule.match_count} matched
                    </span>
                    <button
                      onClick={() => {
                        if (editingRuleId === rule.id) {
                          setEditingRuleId(null);
                        } else {
                          startEditing(rule);
                        }
                      }}
                      className={`px-3 py-1 rounded text-xs font-mono transition-colors border ${
                        editingRuleId === rule.id
                          ? 'bg-gray-600 text-gray-300 border-gray-500'
                          : 'bg-transparent text-cyan-400 border-cyan-500/50 hover:bg-cyan-500/10'
                      }`}
                    >
                      {editingRuleId === rule.id ? 'cancel' : 'edit'}
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="bg-transparent text-red-400/70 border border-red-500/30 px-3 py-1 rounded text-xs font-mono hover:text-red-400 hover:border-red-500/50 transition-colors"
                    >
                      delete
                    </button>
                  </div>
                </div>

                {/* Edit form (inline) */}
                {editingRuleId === rule.id && (
                  <div className="px-5 py-4 bg-gray-900/50 border-t border-gray-700/30">
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className={tw.label}>
                          Payee Contains (substring, case-insensitive)
                        </label>
                        <input type="text" value={editPayee} onChange={e => setEditPayee(e.target.value)}
                          placeholder="Leave empty to skip" className={tw.input} />
                      </div>
                      <div>
                        <label className={tw.label}>
                          Account Name Contains
                        </label>
                        <input type="text" value={editAccount} onChange={e => setEditAccount(e.target.value)}
                          placeholder="Leave empty to skip" className={tw.input} />
                      </div>
                      <div>
                        <label className={tw.label}>
                          Category Contains
                        </label>
                        <input type="text" value={editCategory} onChange={e => setEditCategory(e.target.value)}
                          placeholder="Leave empty to skip" className={tw.input} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className={tw.label}>
                          Amount Min ($)
                        </label>
                        <input type="number" value={editAmountMin} onChange={e => setEditAmountMin(e.target.value)}
                          placeholder="No minimum" step="0.01" min="0" className={tw.input} />
                      </div>
                      <div>
                        <label className={tw.label}>
                          Amount Max ($)
                        </label>
                        <input type="number" value={editAmountMax} onChange={e => setEditAmountMax(e.target.value)}
                          placeholder="No maximum" step="0.01" min="0" className={tw.input} />
                      </div>
                      <div>
                        <label className={tw.label}>
                          Note/Keyword Contains
                        </label>
                        <input type="text" value={editNote} onChange={e => setEditNote(e.target.value)}
                          placeholder="Leave empty to skip" className={tw.input} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className={tw.label}>
                          Assign to Property
                        </label>
                        <select value={editPropertyId} onChange={e => setEditPropertyId(parseInt(e.target.value) || 0)} className={tw.select}>
                          <option value={0}>-- Select Property --</option>
                          {properties.map(p => (
                            <option key={p.id} value={p.id}>{p.address || p.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={tw.label}>
                          Label (optional)
                        </label>
                        <input type="text" value={editLabel} onChange={e => setEditLabel(e.target.value)}
                          placeholder="Description..." className={tw.input} />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={handleSaveEdit} className={tw.btnPrimary}>
                        save changes
                      </button>
                      <button onClick={() => setEditingRuleId(null)} className={tw.btnSecondary}>
                        cancel
                      </button>
                    </div>

                    {/* Preview of what this rule matches */}
                    <div className="mt-3 px-3.5 py-2.5 bg-gray-800/80 rounded border border-gray-700/50">
                      <span className="text-gray-600 text-xs font-mono">Preview: </span>
                      <span className="text-gray-400 text-xs font-mono">
                        {buildRuleDescription({
                          ...rule,
                          payee_contains: editPayee || null,
                          account_name_contains: editAccount || null,
                          category_contains: editCategory || null,
                          amount_min: editAmountMin ? parseFloat(editAmountMin) : null,
                          amount_max: editAmountMax ? parseFloat(editAmountMax) : null,
                          note_contains: editNote || null,
                          property_id: editPropertyId,
                        })}
                        {editPropertyId > 0 ? ` → ${getPropertyName(editPropertyId)}` : ''}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PocketSmithRules;
